import React, { useState, useEffect } from 'react';
import { Bell, X, AlertCircle, CheckCircle } from 'lucide-react';
import { 
  checkNotificationPermission, 
  renewNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPushNotifications
} from '../lib/notifications';

export function NotificationPermissionBanner() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<{
    permission: NotificationPermission;
    needsRenewal: boolean;
    hasValidSubscription: boolean;
  } | null>(null);

  useEffect(() => {
    checkPermissions();
    
    // Check permissions when the page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkPermissions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const checkPermissions = async () => {
    try {
      // Check if banner was dismissed this session
      const wasDismissed = sessionStorage.getItem('notification-banner-dismissed');
      if (wasDismissed) {
        setShow(false);
        return;
      }

      const status = await checkNotificationPermission();
      setPermissionStatus(status);
      
      // Show banner if permissions need attention
      const shouldShow = (
        status.permission === 'default' || // Never asked
        (status.permission === 'granted' && status.needsRenewal) // Granted but subscription lost
      );
      
      setShow(shouldShow);
    } catch (error) {
      console.error('Error checking notification permissions:', error);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      if (permissionStatus?.permission === 'granted' && permissionStatus.needsRenewal) {
        // Just renew the subscription
        const success = await renewNotificationPermission();
        if (success) {
          setShow(false);
          await checkPermissions();
        } else {
          setError('فشل في تجديد إعدادات الإشعارات');
        }
      } else {
        // Request permission and set up notifications
        const permission = await requestNotificationPermission();
        
        if (permission === 'granted') {
          const registration = await registerServiceWorker();
          await subscribeToPushNotifications(registration);
          setShow(false);
          await checkPermissions();
        } else {
          setError('تم رفض إذن الإشعارات');
        }
      }
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError('حدث خطأ أثناء تفعيل الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    // Remember dismissal for this session
    sessionStorage.setItem('notification-banner-dismissed', 'true');
  };

  // Don't show if not needed
  if (!show) {
    return null;
  }

  const getBannerContent = () => {
    if (permissionStatus?.permission === 'granted' && permissionStatus.needsRenewal) {
      return {
        title: 'إعادة تفعيل الإشعارات',
        message: 'يبدو أن إعدادات الإشعارات تحتاج إلى تجديد للمتابعة في تلقي التحديثات',
        buttonText: 'تجديد الإشعارات',
        icon: <AlertCircle className="text-orange-500" size={20} />,
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      };
    } else {
      return {
        title: 'تفعيل الإشعارات',
        message: 'احصل على إشعارات فورية عند إضافة معاملات جديدة أو تحديثات مهمة',
        buttonText: 'تفعيل الإشعارات',
        icon: <Bell className="text-blue-500" size={20} />,
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      };
    }
  };

  const content = getBannerContent();

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${content.bgColor} ${content.borderColor} border-b p-4 animate-fade-in`}>
      <div className="max-w-md mx-auto flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          {content.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-800 mb-1">
            {content.title}
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            {content.message}
          </p>
          
          {error && (
            <div className="mb-3 flex items-center space-x-2 text-red-600">
              <AlertCircle size={14} />
              <span className="text-xs">{error}</span>
            </div>
          )}
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleEnableNotifications}
              disabled={loading}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'جاري التفعيل...' : content.buttonText}
            </button>
            
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              تجاهل
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}